import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getShowcase, BACKEND, subscribeDigest } from "../lib/api";
import { ArrowRight, Mail, Check, Pause, Play, Sun } from "lucide-react";

const fmtRelative = (iso) => {
  if (!iso) return "";
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch (e) { return ""; }
};

export default function LiveDemoHero() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [subErr, setSubErr] = useState("");
  const [subLoading, setSubLoading] = useState(false);

  useEffect(() => {
    getShowcase().then(setData).catch(() => {});
  }, []);

  useEffect(() => {
    if (!data?.featured?.length || paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % data.featured.length), 5500);
    return () => clearInterval(t);
  }, [data, paused]);

  const featured = data?.featured?.[idx];
  const counters = data?.counters;

  const submitInline = async (e) => {
    e.preventDefault();
    setSubErr("");
    if (!email.trim()) return;
    setSubLoading(true);
    try {
      await subscribeDigest({ email: email.trim(), language: "english", level: "school", delivery_hour_ist: 7 });
      setSubscribed(true);
    } catch (ex) {
      setSubErr("Could not subscribe — try the full form.");
    } finally {
      setSubLoading(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-5 md:px-10 pt-10 md:pt-14 pb-10" data-testid="live-demo-hero">
      {/* Live counters strip */}
      {counters && (
        <div className="card p-4 md:p-5 flex flex-wrap items-center gap-x-6 gap-y-2 mb-8" data-testid="live-counters">
          <div className="flex items-center gap-2 pr-3 border-r border-[#DCD7CE]">
            <span className="relative inline-flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E07A5F] opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#E07A5F]" />
            </span>
            <span className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-bold">Live</span>
          </div>
          <Stat n={counters.learners} label="learners" testid="live-learners" />
          <Stat n={counters.stories} label="stories" testid="live-stories" />
          <Stat n={counters.stories_illustrated} label="illustrated" testid="live-illustrated" />
          <Stat n={counters.quizzes} label="quizzes" testid="live-quizzes" />
          <Stat n={counters.subscribers} label="daily subs" testid="live-subs" />
          <Stat n={counters.languages} label="languages" testid="live-langs" />
        </div>
      )}

      {/* Two-column hero */}
      <div className="grid lg:grid-cols-12 gap-8 items-center">
        {/* Left: copy + inline subscribe */}
        <div className="lg:col-span-6">
          <span className="badge" data-testid="hero-badge">Live demo · open data</span>
          <h1 className="font-heading mt-4 text-4xl sm:text-5xl lg:text-[64px] font-extrabold tracking-tight leading-[1.04]">
            <span className="text-[#1A1A1A]">Mythology meets</span><br />
            <span className="text-[#E07A5F]">science</span>, <span className="text-[#3A5A40]">in stories.</span>
          </h1>
          <p className="mt-5 text-[#4A4A4A] text-lg max-w-xl">
            EduSphere AI Companion blends the warmth of <em className="font-story text-[#1A1A1A]">timeless world storytelling</em> with
            50 hands-on STEM models — Newton's Disc, Periscope, Constellation Viewer and more.
            Free, illustrated, and in your language.
          </p>

          {/* Inline subscribe */}
          {!subscribed ? (
            <form onSubmit={submitInline} className="mt-6 flex flex-col sm:flex-row gap-2 max-w-md" data-testid="hero-subscribe-form">
              <input
                type="email"
                placeholder="your@email.com — get tomorrow's story"
                className="input-field flex-1"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="hero-subscribe-email"
              />
              <button type="submit" disabled={subLoading} className="btn-primary inline-flex items-center justify-center gap-2 whitespace-nowrap disabled:opacity-60" data-testid="hero-subscribe-submit">
                <Mail size={14} /> {subLoading ? "Adding…" : "Subscribe free"}
              </button>
            </form>
          ) : (
            <div className="mt-6 inline-flex items-center gap-2 bg-[#EAECE6] text-[#3A5A40] border border-[#3A5A40]/40 rounded-full px-4 py-2 text-sm font-semibold" data-testid="hero-subscribe-confirmed">
              <Check size={16} /> You're in! Tomorrow's story will land in your inbox.
            </div>
          )}
          {subErr && <div className="text-[#D9534F] text-sm mt-2" data-testid="hero-subscribe-error">{subErr}</div>}

          <div className="mt-5 flex flex-wrap gap-3" data-testid="hero-secondary-cta">
            <Link to="/today" className="btn-outline inline-flex items-center gap-2" data-testid="hero-cta-today">
              <Sun size={14} /> Today's story
            </Link>
            <Link to="/chat" className="btn-outline inline-flex items-center gap-2" data-testid="hero-cta-chat">
              Try the tutor <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        {/* Right: rotating featured story card */}
        <div className="lg:col-span-6 lg:pl-4" data-testid="hero-featured-wrap">
          {featured ? (
            <Link to={`/share/story/${featured.id}`} className="block card overflow-hidden fadeIn" data-testid="hero-featured-card" key={featured.id}>
              {featured.illustration_url && (
                <div className="aspect-[4/3] bg-[#FBE7DF]">
                  <img
                    src={`${BACKEND}${featured.illustration_url}`}
                    alt={featured.title}
                    className="w-full h-full object-cover"
                    data-testid="hero-featured-image"
                  />
                </div>
              )}
              <div className="p-5 md:p-6">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold flex items-center gap-2">
                  <span>Featured · {featured.topic || "Story"}</span>
                  <span className="text-[#4A4A4A]">· {fmtRelative(featured.created_at)}</span>
                </div>
                <h3 className="font-story text-2xl md:text-3xl font-semibold text-[#1A1A1A] mt-2 leading-tight">{featured.title}</h3>
                <p className="text-sm text-[#4A4A4A] mt-2 line-clamp-2">{featured.snippet}</p>
                {featured.moral && (
                  <div className="mt-3 inline-block bg-[#EAECE6] text-[#3A5A40] text-xs font-semibold px-2.5 py-1 rounded-full border border-[#DCD7CE]">
                    Moral · {featured.moral.length > 60 ? featured.moral.slice(0, 57) + "…" : featured.moral}
                  </div>
                )}
              </div>
            </Link>
          ) : (
            <div className="card aspect-[4/3] grid place-items-center text-[#4A4A4A]">Loading featured stories…</div>
          )}

          {/* Carousel controls */}
          {data?.featured?.length > 1 && (
            <div className="mt-4 flex items-center justify-between" data-testid="hero-carousel-controls">
              <div className="flex gap-1.5">
                {data.featured.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIdx(i)}
                    aria-label={`Show featured story ${i + 1}`}
                    data-testid={`hero-dot-${i}`}
                    className={`h-2 rounded-full transition-all ${i === idx ? "w-6 bg-[#E07A5F]" : "w-2 bg-[#DCD7CE]"}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setPaused((p) => !p)}
                aria-label={paused ? "Resume rotation" : "Pause rotation"}
                data-testid="hero-pause-toggle"
                className="text-xs text-[#4A4A4A] hover:text-[#E07A5F] inline-flex items-center gap-1.5"
              >
                {paused ? <><Play size={12} /> Resume</> : <><Pause size={12} /> Pause</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

const Stat = ({ n, label, testid }) => (
  <div className="flex items-baseline" data-testid={testid}>
    <span className="font-heading font-extrabold text-xl text-[#1A1A1A] mr-1.5">{n}</span>
    <span className="text-xs text-[#4A4A4A] uppercase tracking-[0.12em]">{label}</span>
  </div>
);
